import React, { useState, ComponentType, useEffect } from 'react';
import axios from 'axios';
import { message } from 'antd';
import { useLocation } from 'react-router-dom';

interface WarehouseData {
    id?: number;
    dealer_code: string;
    dealer_name: string;
    warehouse_name: string;
    address: string;
    contact_person?: string;
    status: number;
}

export interface InjectedWarehouseProps {
    warehouseData?: WarehouseData;
    handleInputChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleSave?: () => Promise<void>;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const withWarehouseLogic = <P extends object>(
    WrappedComponent: ComponentType<P & InjectedWarehouseProps>
) => {
    const ComponentWithWarehouseLogic: React.FC<P> = (props) => {
        // Perbaikan ESLint & TS2339: Gunakan hook useLocation
        const location = useLocation();

        // initialWarehouseData sekarang sesuai interface (id opsional)
        const initialWarehouseData: Omit<WarehouseData, 'id'> & { id?: number } = {
            dealer_code: '',
            dealer_name: '',
            warehouse_name: '',
            address: '',
            contact_person: '',
            status: 1, // Status default
            // id tidak ada di awal
        };

        const [warehouseData, setWarehouseData] = useState<WarehouseData>(initialWarehouseData);

        useEffect(() => {
            // Perbaikan ESLint & TS2339: Gunakan location dari hook
            const incomingData = location.state?.warehouseData as WarehouseData | undefined;
            if (incomingData) {
                console.log("Menerima data dari navigasi:", incomingData);
                setWarehouseData({
                    ...initialWarehouseData, // Mulai dengan default
                    ...incomingData,        // Timpa dengan data yang diterima
                    // Pastikan status adalah number, default ke 1 jika tidak valid
                    status: Number(incomingData.status) || 1,
                    // id akan otomatis ditimpa jika ada di incomingData
                });
                // Bersihkan state dari history setelah digunakan
                window.history.replaceState({}, document.title);
            }
            // Perbaikan ESLint & TS2339: Jadikan location.state sebagai dependency
        }, [location.state]);
        
        const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { name, value } = event.target;
            setWarehouseData(prevData => ({
                ...prevData,
                [name]: value,
            }));
        };

        const handleSave = async () => {
            if (!warehouseData.dealer_code || !warehouseData.warehouse_name) {
                message.error('Dealer Code dan Warehouse Name wajib diisi!');
                return;
            }

            // Tentukan apakah ini update (jika ada id) atau create
            const isUpdate = !!warehouseData.id;

            if (!backendUrl) {
                 console.error("REACT_APP_BACKEND_URL is not defined!");
                 message.error("Konfigurasi backend URL tidak ditemukan.");
                 return;
            }

            const apiUrl = `${backendUrl}/api/warehouse${isUpdate ? `/${warehouseData.id}` : ''}`;
            const method = isUpdate ? 'put' : 'post';

            // Siapkan data yang akan dikirim, pastikan tipe benar (misal: status adalah number)
            const dataToSend = {
                ...warehouseData,
                status: Number(warehouseData.status) // Kirim status sebagai number
            };


            try {
                console.log(`Sending ${method.toUpperCase()} request to ${apiUrl} with data:`, dataToSend);
                const response = await axios({
                    method: method,
                    url: apiUrl,
                    data: dataToSend, // Kirim data yang sudah disiapkan
                });

                console.log('Server response:', response);

                if (response.status === 201 || response.status === 200) { // 201 (Created), 200 (OK for update)
                    message.success(`Data gudang berhasil ${isUpdate ? 'diperbarui' : 'disimpan'}!`);
                    // Reset form ke state awal setelah sukses
                    setWarehouseData(initialWarehouseData);
                    // Opsional: Navigasi kembali atau refresh daftar data
                } else {
                    // Tangani jika status response bukan 200/201 tapi tidak error (jarang terjadi dengan axios)
                    const errorMessage = response.data?.message || `Gagal ${isUpdate ? 'memperbarui' : 'menyimpan'} data gudang.`;
                    message.error(errorMessage);
                }
            } catch (error: any) {
                console.error(`Error ${isUpdate ? 'updating' : 'saving'} warehouse data:`, error);
                // Tangani error dari axios (network error, status 4xx, 5xx)
                const errorMessage = error.response?.data?.message || error.message || 'Terjadi kesalahan saat menyimpan data.';
                message.error(`Gagal ${isUpdate ? 'memperbarui' : 'menyimpan'} data: ${errorMessage}`);
            }
        };
        
        const injectedProps: InjectedWarehouseProps = {
            warehouseData,
            handleInputChange,
            handleSave,
        };

        return <WrappedComponent {...props as P} {...injectedProps} />;
    };

    return ComponentWithWarehouseLogic;
};

export default withWarehouseLogic;